import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { authFetch } from "../../../api";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  Globe,
  MoveLeft,
  MoveRight,
  Notebook,
  ShieldCheck,
} from "lucide-react";

const QUARTERS = [
  { id: 1, title: "Explore Your Interests" },
  { id: 2, title: "Skill Discovery & Practice" },
  { id: 3, title: "Application & Exposure" },
  { id: 4, title: "Reflection & Growth" },
];

export default function QuarterTimeline() {
  const userId = localStorage.getItem("userId");
  const completedClass = Number(localStorage.getItem("completedClass") || 0);

  const [openModal, setOpenModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeQuarter, setActiveQuarter] = useState(1);
  const [completed, setCompleted] = useState([]);
  const [selections, setSelections] = useState({});
  const location = useLocation();
  const { classID, stream } = location.state || {};
  const [timelineData, setTimelineData] = useState([]);
  const [enrolledClass, setEnrolledClass] = useState(null);
  const navigate = useNavigate();
  const currentClass = Number(classID);
  const nextClass = currentClass + 1;

  const selectedCount = selections[activeQuarter]?.length || 0;
  const isLast = activeQuarter === 4;

  const [hasTask, setHasTask] = useState(false);

  const check = async () => {
    try {
      const res = await authFetch("timeline/check-user-assigned-tasks", {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      setHasTask(data?.data > 0);
    } catch (err) {
      console.log(err);
    }
  };
  const checkClass = async () => {
    try {
      const res = await authFetch("timeline/show-student-enrolled-class", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      setEnrolledClass(result.data.student_enrolled_for);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    check();
    checkClass();
  }, []);

  const toggleSelect = (id) => {
    setSelections((prev) => {
      const list = prev[activeQuarter] || [];
      return {
        ...prev,
        [activeQuarter]: list.includes(id)
          ? list.filter((x) => x !== id)
          : [...list, id],
      };
    });
  };

  const handleNext = () => {
    if (selectedCount < 4) return;

    if (!completed.includes(activeQuarter)) {
      setCompleted([...completed, activeQuarter]);
    }

    if (!isLast) {
      setActiveQuarter(activeQuarter + 1);
    }
  };

  const fetchData = async () => {
    try {
      const endpoint = "timeline/show-all-data-by-enrollment";

      const bodyData = {
        student_enrolled_for: Number(classID),
        stream: Number(stream),
        user_id: Number(userId),
      };

      const response = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(bodyData),
        headers: { "Content-Type": "application/json" },
      });

      const result = await response.json();

      //  store API data
      setTimelineData(result?.data || []);
    } catch (err) {
      console.error("Error fetching timeline data:", err);
    }
  };

  const quarterTasks = timelineData.filter(
    (task) => task.quarter === activeQuarter,
  );

  useEffect(() => {
    const init = async () => {
      try {
        const res = await authFetch("timeline/check-user-assigned-tasks", {
          method: "POST",
          body: JSON.stringify({ user_id: userId }),
          headers: { "Content-Type": "application/json" },
        });

        const result = await res.json();
        const taskStatus = result?.data > 0;

        setHasTask(taskStatus);

        fetchData(taskStatus);
      } catch (err) {
        console.error(err);
      }
    };

    init();
  }, []);

  const handleGenerateTimeline = async () => {
    try {
      // 🔹 Convert selections object into required tasks array
      const tasksPayload = Object.keys(selections).flatMap((quarter) =>
        selections[quarter].map((taskId) => ({
          task_id: taskId,
          class_id: classID,
          quarter: Number(quarter),
        })),
      );

      const payload = {
        user_id: Number(userId),
        tasks: tasksPayload,
      };

      const res = await authFetch("timeline/add-customized-data", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();
      console.log("Timeline generation response:", data);
      // ✅ Navigate if already assigned
      if (
        data?.success &&
        data?.message === "All tasks already assigned to user"
      ) {
        navigate("/sap/automated", {
          state: {
            classID: classID,
            stream: stream,
          },
        });
        return;
      }

      // Optional: also navigate if success true
      if (data?.success) {
        navigate("/sap/automated", {
          state: {
            classID: classID,
            stream: stream,
          },
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleBack = () => {
    if (activeQuarter > 1) {
      setActiveQuarter(activeQuarter - 1);
    }
  };

  return (
    <div className="w-full max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
      {/* Headerz */}
      <div className="relative font-inter bg-gradient-to-r from-[#5F6EB4] to-[#C059C0] rounded-xl p-4 sm:p-6 lg:p-8 text-white">
        <h1 className="text-2xl font-bold">
          Create Your Quarter-Wise Timeline
        </h1>
        <p className="mt-2 text-sm opacity-90">
          Select activities for each quarter to create a personalized,
          skill-focused learning journey.
        </p>

        {/* Class Tabs */}
        <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
          {[1, 2, 3, 4]
            .filter((num) => num >= enrolledClass)
            .map((num) => {
              // const isLocked = num !== currentClass;
              const isActive = num === currentClass;

              return (
                <button
                  key={num}
                  className={`relative px-4 py-2 rounded-xl text-sm font-semibold
                ${isActive ? "bg-white text-black" : "bg-white/50"}
              `}
                  // disabled={isLocked}
                >
                  Class {num + 8}
                  {/* {isLocked && (
                    <img
                      src="/timeline/lock.png"
                      alt="locked"
                      className="absolute -top-1 -right-1 w-4"
                    />
                  )} */}
                </button>
              );
            })}
        </div>
      </div>

      {/* Quarter Stepper */}
      <div className="mt-10 w-full px-1 sm:px-4">
        <div className="relative flex items-center justify-between">
          {/*  SINGLE BACKGROUND LINE */}
          <div className="absolute top-[18px] left-[12.5%] w-[75%] h-[2px] bg-gray-300 z-0" />
          {QUARTERS.map((q) => {
            const isCompleted = completed.includes(q.id);
            const isActive = activeQuarter === q.id;

            return (
              <div
                key={q.id}
                className="relative z-10 flex flex-col items-center flex-1"
              >
                {/*  CIRCLE */}
                <div
                  className={`w-10 h-10 rounded-full border-2 flex items-center justify-center
            ${
              isCompleted
                ? "bg-[#007E08] border-[#007E08] text-white"
                : isActive
                  ? "border-blue-500 bg-blue-100"
                  : "bg-gray-300 border-gray-300"
            }`}
                >
                  {isCompleted ? "✓" : isActive ? "🔵" : ""}
                </div>

                {/* TITLE */}
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700">
                  Quarter {q.id}
                </span>

                {/* STATUS */}
                <span
                  className={`mt-1 px-3 py-1 rounded-md text-[10px] sm:text-xs whitespace-nowrap
            ${
              isCompleted
                ? "bg-[#ECFFE3] border border-green-600"
                : isActive
                  ? "bg-[#E5FDFF] border border-blue-500"
                  : "bg-gray-200 border border-gray-300"
            }`}
                >
                  {isCompleted
                    ? "Completed"
                    : isActive
                      ? "In Progress"
                      : "Pending"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quarter Content */}
      <div className="mt-10 bg-white border rounded-2xl p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">
              Quarter {activeQuarter}: {QUARTERS[activeQuarter - 1].title}
            </h2>
            <p className="mt-1 text-gray-600 text-sm max-w-xl">
              Begin your foundation discovery journey by exploring various
              interests & joining activities.
            </p>
          </div>

          <span className="bg-[#309D7E] text-white px-4 py-2 rounded-xl text-sm font-semibold shadow">
            Select Minimum 4
          </span>
        </div>

        {/* Activities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {quarterTasks.map((task) => {
            const checked =
              selections[activeQuarter]?.includes(task.id) || false;

            return (
              <div
                key={task.id}
                onClick={() => toggleSelect(task.id)}
                className={`relative rounded-2xl p-6 bg-gradient-to-r 
  from-[#FFFDF9] to-[#FFF3E0] border border-[#F3E6CF] cursor-pointer
  ${checked ? "ring-2 ring-[#309D7E]" : ""}
`}
              >
                {/* Checkbox */}
                <div className="absolute top-4 right-4">
                  <input
                    type="checkbox"
                    checked={checked}
                    readOnly
                    onClick={() => toggleSelect(task.id)}
                    className="h-4 w-4 accent-green-600"
                  />
                </div>

                {/* Title */}
                <h4 className="font-bold text-lg text-[#0E101A]">
                  {task.task_title}
                </h4>

                {/* Short Description */}
                <p className="mt-2 text-sm text-[#5F6368]">
                  {task.task_description?.slice(0, 90)}...
                  <button
                    onClick={() => {
                      setSelectedTask(task);
                      setOpenModal(true);
                    }}
                    className="ml-1 text-[#6A1B9A] text-sm font-semibold hover:underline"
                  >
                    View More
                  </button>
                </p>
              </div>
            );
          })}
        </div>

        {/* Footer Buttons */}
        <div className="mt-10 flex flex-col sm:flex-row justify-between gap-4">
          <button
            onClick={handleBack}
            disabled={activeQuarter === 1}
            className={`w-full sm:w-auto px-8 py-2 border rounded-xl text-sm font-semibold 
    ${
      activeQuarter === 1
        ? "text-gray-400 border-gray-300 cursor-not-allowed"
        : "text-gray-700 hover:bg-gray-100"
    }
  `}
          >
            Back
          </button>

          <button
            onClick={isLast ? handleGenerateTimeline : handleNext}
            disabled={selectedCount < 4}
            className={`w-full sm:w-auto px-10 sm:px-14 py-3 rounded-xl font-semibold text-sm text-white shadow
    ${
      selectedCount < 4
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-[#572575] hover:bg-purple-800"
    }
  `}
          >
            {isLast ? "Generate Timeline" : "Save & Next"}
          </button>
        </div>
      </div>
      <ActivityModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        data={selectedTask}
      />
    </div>
  );
}

const ActivityModal = ({ open, onClose, data }) => {
  const [index, setIndex] = useState(0);

  if (!open || !data) return null;

  const subActivities = data.subActivities || [];
  const currentSub = subActivities[index];

  const handleNext = () => {
    setIndex((prev) => (prev === subActivities.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setIndex((prev) => (prev === 0 ? subActivities.length - 1 : prev - 1));
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-sm font-inter">
      <div className="relative w-[95%] sm:w-[92%] max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* HEADER */}
        <div className="w-full px-6 py-4 border-b flex items-center justify-between bg-white sticky top-0 z-10">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            {data.task_title}
          </h2>

          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide">
          {/* DESCRIPTION */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {data.task_description}
            </p>
          </div>

          {/* COURSE CARD */}
          {currentSub && (
            <div className="relative bg-[#F9F4EE] rounded-2xl p-6 border">
              {/* PREV BUTTON */}
              {subActivities.length > 1 && (
                <button
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 
                 w-10 h-10 rounded-full bg-gray-200 shadow-md 
                 hover:scale-105 transition flex items-center justify-center"
                >
                  <MoveLeft size={18} />
                </button>
              )}

              {/* NEXT BUTTON */}
              {subActivities.length > 1 && (
                <button
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 
                 w-10 h-10 rounded-full bg-gray-200 shadow-md 
                 hover:scale-105 transition flex items-center justify-center"
                >
                  <MoveRight size={18} />
                </button>
              )}
              <div className="flex flex-col md:flex-row bg-white rounded-2xl shadow-md overflow-hidden min-h-[260px]">
                {/* LEFT IMAGE FULL HEIGHT */}
                <div className="w-full md:w-56 h-48 md:h-auto">
                  <img
                    src="/subActivity.png"
                    alt="sub-activity"
                    className=" inset-0 w-full h-full object-cover"
                  />
                </div>

                {/* RIGHT CONTENT */}
                <div className="flex-1 p-6 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-gray-900 text-lg">
                        {currentSub.sub_activity_name}
                      </h4>
                    </div>

                    <p className="text-sm text-gray-600 mt-3">
                      {currentSub.sub_activity_description}
                    </p>

                    {/* Link */}
                    <a
                      href={`https://${currentSub.link}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-blue-600 block mt-3 underline"
                    >
                      {currentSub.link}
                    </a>

                    {/* Meta Info */}
                    <div className="mt-5 grid grid-cols-2 gap-y-3 gap-x-6 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Clock size={16} />
                        <span>Duration - {currentSub.duration} hours</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <ShieldCheck size={16} />
                        <span>Certificate - {data.certificate_name}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Notebook size={16} />
                        <span>Assessment - {currentSub.no_of_assessment}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Globe size={16} />
                        <span>Language - {currentSub.project_language}</span>
                      </div>
                    </div>

                    {/* Tools */}
                    <div className="mt-4 text-sm">
                      <span className="font-medium">Tools Required: </span>
                      {currentSub.tools_required}
                    </div>
                  </div>
                </div>
              </div>

              {/* DOTS */}
              {subActivities.length > 1 && (
                <div className="flex justify-center gap-2 mt-5">
                  {subActivities.map((_, i) => (
                    <span
                      key={i}
                      className={`transition-all duration-300 ${
                        i === index
                          ? "w-6 h-1.5 bg-orange-400"
                          : "w-2 h-2 bg-gray-300"
                      } rounded-full`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
